/** API resource path for Nest `@Controller("employeeSalaryHistory")`. */
export const EMPLOYEE_SALARY_HISTORY_LOCATION = "employeeSalaryHistory";

export type SalaryHistoryRow = {
  id: string;
  employeeId: number;
  monthlyGross: number;
  effectiveFrom: string;
  notes?: string | null;
};

export type SalaryHistoryFormValues = {
  monthlyGross: string;
  effectiveFrom: string;
  notes: string;
};

export const EMPTY_SALARY_HISTORY_FORM: SalaryHistoryFormValues = {
  monthlyGross: "",
  effectiveFrom: "",
  notes: "",
};

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

export type SalaryHistoryCreatePayload = {
  employeeId: number;
  monthlyGross: number;
  effectiveFrom: string;
  notes?: string | null;
};

/**
 * Validate add-salary form. Returns an error message or a create payload.
 */
export function validateSalaryHistoryForm(
  employeeId: number,
  values: SalaryHistoryFormValues
): { ok: true; payload: SalaryHistoryCreatePayload } | { ok: false; error: string } {
  if (!Number.isFinite(employeeId) || employeeId < 1) {
    return { ok: false, error: "Employee is required." };
  }

  const grossRaw = values.monthlyGross.trim();
  if (!grossRaw) {
    return { ok: false, error: "Monthly gross is required." };
  }
  const monthlyGross = Number(grossRaw);
  if (!Number.isInteger(monthlyGross) || monthlyGross < 1) {
    return { ok: false, error: "Monthly gross must be a whole number of at least 1." };
  }

  const effectiveFrom = values.effectiveFrom.trim().slice(0, 10);
  if (!ISO_DATE.test(effectiveFrom)) {
    return { ok: false, error: "Effective from must be a valid date (YYYY-MM-DD)." };
  }

  const notesTrimmed = values.notes.trim();
  return {
    ok: true,
    payload: {
      employeeId,
      monthlyGross,
      effectiveFrom,
      notes: notesTrimmed ? notesTrimmed : null,
    },
  };
}

export function formatMonthlyGrossInr(value: number | null | undefined): string {
  if (value == null || !Number.isFinite(value)) return "—";
  return `₹${value.toLocaleString("en-IN")}`;
}

/** Extract list rows from react-admin / Nest list responses. */
export function extractSalaryHistoryRows(data: unknown): SalaryHistoryRow[] {
  if (!data || typeof data !== "object") return [];
  const body = data as { rows?: unknown; data?: unknown };
  if (Array.isArray(body.rows)) return body.rows as SalaryHistoryRow[];
  if (Array.isArray(body.data)) return body.data as SalaryHistoryRow[];
  if (Array.isArray(data)) return data as SalaryHistoryRow[];
  return [];
}
